import React, { useState, useEffect, useRef } from 'react';
import {
  Card, CardContent, CardHeader, Typography, TextField, Button,
  Grid, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Box, IconButton, Tooltip, Collapse, FormControlLabel, Switch
} from '@mui/material';
import { Engineering, Calculate, Add, Delete } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = '/api/slab-calculator';

const EDGE_LABELS = { 1: 'x=0', 2: 'x=L', 3: 'y=W', 4: 'y=0' };
const EDGE_TYPES = ['pinned', 'fixed', 'free'];

const formatApiError = (err) => {
  const data = err.response?.data;
  if (!data) return err.message || 'Analysis failed';
  if (typeof data.error === 'string') return data.error;
  if (typeof data === 'object') {
    return Object.entries(data)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join('; ');
  }
  return String(data);
};

// Format number with space as thousands separator (e.g. 1 234 567.89)
// For |v| >= 1e6 use exponent form (e.g. 2e10) for easier editing
// decimals: 0+ = fix decimal places; -1 = grouping only, preserve decimals as-is
const formatWithSpaces = (v, decimals = 2) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  if (Math.abs(n) >= 1e6 || (n !== 0 && Math.abs(n) < 1e-2)) {
    return n.toExponential(2).replace(/e\+?/, 'e').replace(/\.0+e/, 'e');
  }
  const fixed = decimals >= 0 ? n.toFixed(decimals) : String(n);
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
};

// Parse input: spaces, comma-as-decimal, and exponent form (e.g. 2e10)
const parseWithSpaces = (s) => {
  if (s === null || s === undefined || s === '') return null;
  const cleaned = String(s).replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

// Beam support stiffness hints (simply-supported edge beam, EN 13670 wet-stage order)
const REF_EDGE_LINE_LOAD_KN_M = 10; // indicative wet-phase line load on edge beam

/** I (mm⁴) for SS edge beam: δ = L / deflectionRatio under line load q (kN/m). */
const iForEdgeBeamDeflection = (spanM, E_mpa, q_kN_m, deflectionRatio) => {
  if (!spanM || !E_mpa || !deflectionRatio) return 0;
  const Lmm = spanM * 1000;
  const deltaMm = Lmm / deflectionRatio;
  return (5 * q_kN_m * Lmm ** 4) / (384 * E_mpa * deltaMm);
};

const eiKNm2 = (I_mm4, E_mpa) => E_mpa * I_mm4 * 1e-9;

const ssBeamDeflectionMm = (spanM, E_mpa, I_mm4, q_kN_m = REF_EDGE_LINE_LOAD_KN_M) => {
  if (!spanM || !E_mpa || !I_mm4) return 0;
  const Lmm = spanM * 1000;
  return (5 * q_kN_m * Lmm ** 4) / (384 * E_mpa * I_mm4);
};

const beamStiffnessHintRows = (spanM, E_mpa = 33000) => {
  const mk = (label, ratio) => {
    const I = iForEdgeBeamDeflection(spanM, E_mpa, REF_EDGE_LINE_LOAD_KN_M, ratio);
    return {
      label,
      I,
      EI: eiKNm2(I, E_mpa),
      deltaMm: spanM * 1000 / ratio,
      ratio,
    };
  };
  return [
    mk('Stiff (δ ≈ L/1000)', 1000),
    mk('Typical edge beam (δ ≈ L/500)', 500),
    mk('Flexible / casting L/250', 250),
  ];
};

const formatHintI = (v) =>
  v >= 1e6 ? v.toExponential(1).replace(/e\+?/, 'e') : Math.round(v).toLocaleString();

const beamSpanForEdge = (edgeId, L_m, W_m, L_override = 0) => {
  if (L_override > 0) return L_override;
  return edgeId === 1 || edgeId === 2 ? W_m : L_m;
};

const FormattedNumberField = ({ value, onChange, decimalPlaces = 2, ...props }) => {
  const [text, setText] = useState(() => formatWithSpaces(value, decimalPlaces));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setText(formatWithSpaces(value, decimalPlaces));
    }
  }, [value, decimalPlaces, focused]);

  return (
    <TextField
      {...props}
      value={focused ? text : formatWithSpaces(value, decimalPlaces)}
      onFocus={(e) => {
        setFocused(true);
        setText(formatWithSpaces(value, decimalPlaces));
        props.onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const parsed = parseWithSpaces(raw);
        if (parsed !== null) {
          onChange(parsed);
        }
      }}
      onBlur={(e) => {
        setFocused(false);
        const parsed = parseWithSpaces(text);
        if (parsed !== null) {
          onChange(parsed);
          setText(formatWithSpaces(parsed, decimalPlaces));
        } else {
          setText(formatWithSpaces(value, decimalPlaces));
        }
        props.onBlur?.(e);
      }}
    />
  );
};

const SlabInputForm = ({ onAnalysisComplete, formDataRef, initialData, onInitialDataApplied, onInputChange }) => {
  const parseFlexibleFloat = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const val = parseFloat(String(v).replace(',', '.'));
    return isNaN(val) ? 0 : val;
  };

  const [slabFamily, setSlabFamily] = useState('kuorilaatta');
  const [slabTypes, setSlabTypes] = useState({ kuorilaatta: [], hollow_core: [], steel_composite: [] });
  const [tendonPresets, setTendonPresets] = useState([]);
  const [slabType, setSlabType] = useState('KL150');
  const [formData, setFormData] = useState({
    L_m: 8,
    W_m: 10,
    h_top_mm: 220,
    n_tendons: 6,
    diameter_tendon_mm: 9.3,
    tension_mpa: 1000,
    tendon_c_nom_mm: 30,
    tendon_preset: '9.3-1860',
    fpk: 1860,
    fp01k: 1640,
    top_slab_cover_bottom_mm: 15,
    rebar_x: { diameter_mm: 12, spacing_mm: 150 },
    rebar_y: { diameter_mm: 12, spacing_mm: 150 },
    sdl_display: 1.5, // UI: positive = downward; sent as negative to API
    uniform_live_load_kN_m2: 0, // Uniform live load over full area (positive = downward)
    mesh_density: 5,
    point_loads_var: [],
    edge_conditions: { 1: 'pinned', 2: 'pinned', 3: 'free', 4: 'free' },
    beam_supports: null, // { 1: { I_mm4, E_mpa, L_m }, 2: {...}, ... } for elastic beam support & compression-only contact
    bearing_k_per_m: 1e6,
    bearing_capacity_per_m: 5000,
    interior_support_y_m: [],
    wq_composite_mode: false,
    sheet_thickness_mm: 0.9,
    longitudinal_shear_resistance_kN_m: 0,
    wq_beam: {
      web_width_mm: 200,
      bottom_flange_width_mm: 600,
      web_depth_mm: 400,
      web_thickness_mm: 12,
      bottom_flange_thickness_mm: 20,
      steel_grade: 'S355',
      propped_during_construction: true,
    },
    t0_days: 7,
    t_service_days: 60,
    rh_pct: 50,
    temp_c: 20,
    include_images: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const applyingInitialDataRef = useRef(false);

  useEffect(() => {
    axios.get(`${API_BASE}/slab-types/`).then((r) => setSlabTypes(r.data)).catch(() => {});
    axios.get(`${API_BASE}/tendon-presets/`).then((r) => setTendonPresets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const types = slabTypes[slabFamily] || [];
    if (types.length && !types.find((t) => t.id === slabType)) {
      setSlabType(types[0].id);
    }
  }, [slabFamily, slabTypes, slabType]);

  useEffect(() => {
    if (slabFamily !== 'steel_composite') return;
    const sheet = (slabTypes.steel_composite || []).find((t) => t.id === slabType);
    const rows = sheet?.thicknesses || [];
    if (!rows.length) return;
    const match = rows.some((t) => Number(t.thickness_mm) === Number(formData.sheet_thickness_mm));
    if (!match) {
      setFormData((prev) => ({ ...prev, sheet_thickness_mm: rows[0].thickness_mm }));
    }
  }, [slabFamily, slabType, slabTypes, formData.sheet_thickness_mm]);

  useEffect(() => {
    const p = tendonPresets.find((t) => t.id === formData.tendon_preset);
    if (p) {
      setFormData((prev) => ({
        ...prev,
        diameter_tendon_mm: p.diameter_mm,
        fpk: p.fpk_mpa,
        fp01k: p.fp01k_mpa,
      }));
    }
  }, [formData.tendon_preset, tendonPresets]);

  useEffect(() => {
    if (formDataRef) {
      formDataRef.current = { ...formData, slab_family: slabFamily, slab_type: slabType };
    }
  }, [formData, slabFamily, slabType, formDataRef]);

  useEffect(() => {
    if (!applyingInitialDataRef.current) onInputChange?.();
  }, [formData, slabFamily, slabType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialData) return;
    applyingInitialDataRef.current = true;
    const d = initialData;
    if (d.slab_family) setSlabFamily(d.slab_family);
    if (d.slab_type) setSlabType(d.slab_type);
    const edgeConditions = d.edge_conditions
      ? Object.fromEntries(
          Object.entries(d.edge_conditions).map(([k, v]) => [Number(k), v]),
        )
      : undefined;
    setFormData((prev) => ({
      ...prev,
      L_m: d.L_m ?? prev.L_m,
      W_m: d.W_m ?? prev.W_m,
      h_top_mm: d.h_top_mm ?? prev.h_top_mm,
      n_tendons: d.n_tendons ?? prev.n_tendons,
      diameter_tendon_mm: d.diameter_tendon_mm ?? prev.diameter_tendon_mm,
      tension_mpa: d.tension_mpa ?? prev.tension_mpa,
      tendon_c_nom_mm: d.tendon_c_nom_mm ?? prev.tendon_c_nom_mm,
      top_slab_cover_bottom_mm: d.top_slab_cover_bottom_mm ?? prev.top_slab_cover_bottom_mm,
      tendon_preset: d.tendon_preset ?? prev.tendon_preset,
      rebar_x: d.rebar_x ?? prev.rebar_x,
      rebar_y: d.rebar_y ?? prev.rebar_y,
      sdl_display: d.superimposed_dead_load_kN_m2 != null ? Math.abs(d.superimposed_dead_load_kN_m2) : prev.sdl_display,
      uniform_live_load_kN_m2: d.uniform_live_load_kN_m2 ?? prev.uniform_live_load_kN_m2 ?? 0,
      mesh_density: d.mesh_density ?? prev.mesh_density,
      point_loads_var: d.point_loads_var ?? prev.point_loads_var,
      edge_conditions: edgeConditions ?? prev.edge_conditions,
      beam_supports: d.beam_supports ?? prev.beam_supports,
      bearing_k_per_m: d.bearing_k_per_m ?? prev.bearing_k_per_m,
      bearing_capacity_per_m: d.bearing_capacity_per_m ?? prev.bearing_capacity_per_m,
      interior_support_y_m: d.interior_support_y_m ?? prev.interior_support_y_m ?? [],
      wq_composite_mode: d.wq_composite_mode ?? prev.wq_composite_mode,
      sheet_thickness_mm: d.sheet_thickness_mm ?? prev.sheet_thickness_mm,
      longitudinal_shear_resistance_kN_m: d.longitudinal_shear_resistance_kN_m ?? prev.longitudinal_shear_resistance_kN_m,
      wq_beam: d.wq_beam ? { ...prev.wq_beam, ...d.wq_beam } : prev.wq_beam,
      t0_days: d.t0_days ?? prev.t0_days,
      t_service_days: d.t_service_days ?? prev.t_service_days,
      rh_pct: d.rh_pct ?? prev.rh_pct,
      temp_c: d.temp_c ?? prev.temp_c,
      include_images: d.include_images ?? prev.include_images,
    }));
    onInitialDataApplied?.();
    const t = setTimeout(() => { applyingInitialDataRef.current = false; }, 0);
    return () => clearTimeout(t);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPointLoad = () => {
    setFormData((prev) => ({
      ...prev,
      point_loads_var: [
        ...prev.point_loads_var,
        { x: 4000, y: 5000, Fz: 100, width_x: 400, width_y: 400, load_type: 'variable' },
      ],
    }));
  };

  const removePointLoad = (i) => {
    setFormData((prev) => ({
      ...prev,
      point_loads_var: prev.point_loads_var.filter((_, j) => j !== i),
    }));
  };

  const updatePointLoad = (i, field, value) => {
    setFormData((prev) => ({
      ...prev,
      point_loads_var: prev.point_loads_var.map((pl, j) =>
        j === i ? { ...pl, [field]: field === 'load_type' ? value : parseFlexibleFloat(value) } : pl
      ),
    }));
  };

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        L_m: formData.L_m,
        W_m: formData.W_m,
        slab_type: slabType,
        slab_family: slabFamily,
        h_top_mm: formData.h_top_mm,
        n_tendons: formData.n_tendons,
        diameter_tendon_mm: formData.diameter_tendon_mm,
        tendon_preset: formData.tendon_preset,
        fpk: formData.fpk,
        fp01k: formData.fp01k,
        tension_mpa: formData.tension_mpa,
        tendon_c_nom_mm: formData.tendon_c_nom_mm,
        top_slab_cover_bottom_mm: formData.top_slab_cover_bottom_mm,
        rebar_x: formData.rebar_x,
        rebar_y: formData.rebar_y,
        point_loads_var: formData.point_loads_var,
        superimposed_dead_load_kN_m2: -(Math.abs(formData.sdl_display || 0)), // Convert positive UI to negative (downward)
        uniform_live_load_kN_m2: Math.max(0, parseFlexibleFloat(formData.uniform_live_load_kN_m2)),
        mesh_density: formData.mesh_density,
        edge_conditions: formData.edge_conditions,
        beam_supports: formData.beam_supports,
        bearing_k_per_m: formData.bearing_k_per_m,
        bearing_capacity_per_m: formData.bearing_capacity_per_m,
        interior_support_y_m: formData.interior_support_y_m?.length ? formData.interior_support_y_m : null,
        wq_composite_mode: formData.wq_composite_mode,
        sheet_thickness_mm: slabFamily === 'steel_composite' ? formData.sheet_thickness_mm : null,
        longitudinal_shear_resistance_kN_m:
          slabFamily === 'steel_composite' && Number(formData.longitudinal_shear_resistance_kN_m) > 0
            ? Number(formData.longitudinal_shear_resistance_kN_m)
            : null,
        wq_beam: formData.wq_composite_mode ? formData.wq_beam : null,
        t0_days: formData.t0_days,
        t_service_days: formData.t_service_days,
        rh_pct: formData.rh_pct,
        temp_c: formData.temp_c,
        include_images: !!formData.include_images,
      };
      const res = await axios.post(`${API_BASE}/analyze/`, payload);
      onAnalysisComplete?.(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Engineering />
        Slab Analysis
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Geometry & Slab Type" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Tooltip title="Span in X-direction (longitudinal)">
                    <FormattedNumberField
                      fullWidth
                      label="Span L (m) — X-direction"
                      value={formData.L_m}
                      onChange={(v) => setFormData({ ...formData, L_m: v })}
                      decimalPlaces={1}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Width in Y-direction (transverse)">
                    <FormattedNumberField
                      fullWidth
                      label="Width W (m) — Y-direction"
                      value={formData.W_m}
                      onChange={(v) => setFormData({ ...formData, W_m: v })}
                      decimalPlaces={1}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Slab Family</InputLabel>
                    <Select
                      value={slabFamily}
                      label="Slab Family"
                      onChange={(e) => setSlabFamily(e.target.value)}
                    >
                      <MenuItem value="kuorilaatta">Kuorilaatta (precast pre-tensioned)</MenuItem>
                      <MenuItem value="hollow_core">Hollow core slabs</MenuItem>
                      <MenuItem value="steel_composite">Steel-composite (profiled sheet, EN 1994)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Slab Type</InputLabel>
                    <Select
                      value={slabType}
                      label="Slab Type"
                      onChange={(e) => setSlabType(e.target.value)}
                    >
                      {(slabTypes[slabFamily] || []).map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {slabFamily === 'steel_composite'
                            ? `${t.name || t.id} (rib ${t.height_mm ?? t.depth_mm} mm)`
                            : `${t.id} (${t.depth_mm} mm, ${t.self_weight_kg_m2} kg/m²)`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {slabFamily === 'steel_composite' && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Sheet thickness (mm)</InputLabel>
                        <Select
                          value={formData.sheet_thickness_mm}
                          label="Sheet thickness (mm)"
                          onChange={(e) => setFormData({ ...formData, sheet_thickness_mm: Number(e.target.value) })}
                        >
                          {(
                            (slabTypes.steel_composite || []).find((t) => t.id === slabType)?.thicknesses || []
                          ).map((t) => (
                            <MenuItem key={t.thickness_mm} value={t.thickness_mm}>
                              {t.thickness_mm} mm ({t.weight_kg_m2} kg/m², {t.steel_grade})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Tooltip title="Design longitudinal shear resistance per metre of span. 0 omits shear connection, so sagging resistance is the sheet alone.">
                        <FormattedNumberField
                          fullWidth
                          label="Longitudinal shear resistance (kN/m)"
                          value={formData.longitudinal_shear_resistance_kN_m}
                          onChange={(v) => setFormData({ ...formData, longitudinal_shear_resistance_kN_m: v })}
                          decimalPlaces={1}
                        />
                      </Tooltip>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormattedNumberField
                    fullWidth
                    label={slabFamily === 'steel_composite' ? 'Concrete above ribs (mm)' : 'Top slab thickness (mm)'}
                    value={formData.h_top_mm}
                    onChange={(v) => setFormData({ ...formData, h_top_mm: v })}
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Tooltip title="Elements per meter. Higher = finer mesh, slower. Aspect ratio L:W preserved.">
                    <FormattedNumberField
                      fullWidth
                      label="Mesh density (elements/m)"
                      value={formData.mesh_density}
                      onChange={(v) => setFormData({ ...formData, mesh_density: v })}
                      decimalPlaces={1}
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title={slabFamily === 'steel_composite' ? 'Reinforcement' : 'Prestress & Reinforcement'} />
            <CardContent>
              <Grid container spacing={2}>
                {slabFamily !== 'steel_composite' && (
                <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Tendon preset</InputLabel>
                    <Select
                      value={formData.tendon_preset}
                      label="Tendon preset"
                      onChange={(e) => setFormData({ ...formData, tendon_preset: e.target.value })}
                    >
                      {tendonPresets.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.id} (φ{t.diameter_mm} mm, Sn={t.area_mm2} mm², fpk={t.fpk_mpa})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Number tendons per precast slab"
                    value={formData.n_tendons}
                    onChange={(v) => setFormData({ ...formData, n_tendons: Math.round(v) || 0 })}
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Tension (MPa)"
                    value={formData.tension_mpa}
                    onChange={(v) => setFormData({ ...formData, tension_mpa: v })}
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Cover tendon (mm)"
                    value={formData.tendon_c_nom_mm}
                    onChange={(v) => setFormData({ ...formData, tendon_c_nom_mm: v })}
                    decimalPlaces={0}
                  />
                </Grid>
                </>
                )}
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Cover rebar (mm)"
                    value={formData.top_slab_cover_bottom_mm}
                    onChange={(v) => setFormData({ ...formData, top_slab_cover_bottom_mm: v })}
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Rebar X φ (mm)"
                    value={formData.rebar_x.diameter_mm}
                    onChange={(v) =>
                      setFormData({
                        ...formData,
                        rebar_x: { ...formData.rebar_x, diameter_mm: v },
                      })
                    }
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Rebar X spacing (mm)"
                    value={formData.rebar_x.spacing_mm}
                    onChange={(v) =>
                      setFormData({
                        ...formData,
                        rebar_x: { ...formData.rebar_x, spacing_mm: v },
                      })
                    }
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Rebar Y φ (mm)"
                    value={formData.rebar_y.diameter_mm}
                    onChange={(v) =>
                      setFormData({
                        ...formData,
                        rebar_y: { ...formData.rebar_y, diameter_mm: v },
                      })
                    }
                    decimalPlaces={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormattedNumberField
                    fullWidth
                    label="Rebar Y spacing (mm)"
                    value={formData.rebar_y.spacing_mm}
                    onChange={(v) =>
                      setFormData({
                        ...formData,
                        rebar_y: { ...formData.rebar_y, spacing_mm: v },
                      })
                    }
                    decimalPlaces={0}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Boundary conditions"
              subheader="Edges 1–2 span supports (x); edges 3–4 are transverse (y). Free edges have no line support."
            />
            <CardContent>
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((edgeId) => (
                  <Grid item xs={6} sm={3} key={edgeId}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Edge {EDGE_LABELS[edgeId]}</InputLabel>
                      <Select
                        value={formData.edge_conditions?.[edgeId] ?? 'free'}
                        label={`Edge ${EDGE_LABELS[edgeId]}`}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            edge_conditions: { ...prev.edge_conditions, [edgeId]: e.target.value },
                          }))
                        }
                      >
                        {EDGE_TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Creep & service age"
              subheader="Precast age at the topping pour, ambient humidity, and curing temperature (EN 1992 Annex B)."
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Tooltip title="Age of the precast plank when the topping is poured. This starts the creep of the plank under its own weight, camber and the wet topping. It is not the age of the topping.">
                    <FormattedNumberField
                      fullWidth
                      label="Precast age at topping pour (days)"
                      value={formData.t0_days}
                      onChange={(v) => setFormData({ ...formData, t0_days: v })}
                      decimalPlaces={0}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Tooltip title="Age of the precast plank at leveling. Creep from the pour to this age uses calendar days.">
                    <FormattedNumberField
                      fullWidth
                      label="Precast age at leveling (days)"
                      value={formData.t_service_days}
                      onChange={(v) => setFormData({ ...formData, t_service_days: v })}
                      decimalPlaces={0}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Tooltip title="Ambient relative humidity during creep, in percent. This is not a substitute for curing temperature.">
                    <FormattedNumberField
                      fullWidth
                      label="RH (%)"
                      value={formData.rh_pct}
                      onChange={(v) => setFormData({ ...formData, rh_pct: v })}
                      decimalPlaces={0}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Tooltip title="Mean precast curing temperature up to the pour, in °C. 20°C is the Annex B reference and leaves the age unchanged. A warmer cure raises the maturity age at the pour and reduces creep.">
                    <FormattedNumberField
                      fullWidth
                      label="Curing temperature (°C)"
                      value={formData.temp_c}
                      onChange={(v) => setFormData({ ...formData, temp_c: v })}
                      decimalPlaces={0}
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Loads" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Tooltip title="SDL = Presumed permanent load. Enter positive for downward (e.g. 1.5 = 1.5 kN/m² down). Stored as negative internally.">
                    <FormattedNumberField
                      fullWidth
                      label="SDL (kN/m²) — Presumed permanent load"
                      value={formData.sdl_display ?? 1.5}
                      onChange={(v) => setFormData({ ...formData, sdl_display: v })}
                      decimalPlaces={-1}
                      helperText="Enter positive for downward load"
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Tooltip title="Uniform live load over full slab area. Enter positive for downward. For combination: 1.5×Q in ULS.">
                    <FormattedNumberField
                      fullWidth
                      label="Uniform live load (kN/m²)"
                      value={formData.uniform_live_load_kN_m2 ?? 0}
                      onChange={(v) => setFormData({ ...formData, uniform_live_load_kN_m2: Math.max(0, v) })}
                      decimalPlaces={-1}
                      inputProps={{ min: 0 }}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" startIcon={<Add />} onClick={addPointLoad}>
                    Add point load
                  </Button>
                </Grid>
                {formData.point_loads_var.map((pl, i) => (
                  <Grid item xs={12} key={i}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Tooltip title="X position (mm), along span">
                        <FormattedNumberField size="small" label="x (mm)" value={pl.x} onChange={(v) => updatePointLoad(i, 'x', v)} sx={{ width: 90 }} decimalPlaces={0} />
                      </Tooltip>
                      <Tooltip title="Y position (mm), along width">
                        <FormattedNumberField size="small" label="y (mm)" value={pl.y} onChange={(v) => updatePointLoad(i, 'y', v)} sx={{ width: 90 }} decimalPlaces={0} />
                      </Tooltip>
                      <Tooltip title="Force (kN). Positive = downward">
                        <FormattedNumberField size="small" label="Fz (kN)" value={pl.Fz} onChange={(v) => updatePointLoad(i, 'Fz', v)} sx={{ width: 90 }} decimalPlaces={1} />
                      </Tooltip>
                      <Tooltip title="Contact width in X (mm). Load is distributed over this area.">
                        <FormattedNumberField size="small" label="wx (mm)" value={pl.width_x} onChange={(v) => updatePointLoad(i, 'width_x', v)} sx={{ width: 80 }} decimalPlaces={0} />
                      </Tooltip>
                      <Tooltip title="Contact width in Y (mm). Uniform load = full slab area (L×W).">
                        <FormattedNumberField size="small" label="wy (mm)" value={pl.width_y} onChange={(v) => updatePointLoad(i, 'width_y', v)} sx={{ width: 80 }} decimalPlaces={0} />
                      </Tooltip>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={pl.load_type || 'variable'}
                          label="Type"
                          onChange={(e) => updatePointLoad(i, 'load_type', e.target.value)}
                        >
                          <MenuItem value="variable">Live (Q)</MenuItem>
                          <MenuItem value="permanent">Dead (G)</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton size="small" onClick={() => removePointLoad(i)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Beam support (optional)"
              subheader="Leave disabled for rigid line support (w=0). When enabled, elastic beams are coupled with compression-only slab–beam contact (zero tension; optional capped bearing capacity)."
            />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.beam_supports}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        const spanM = formData.W_m || 10;
                        const typicalI = iForEdgeBeamDeflection(spanM, 33000, REF_EDGE_LINE_LOAD_KN_M, 500);
                        setFormData((prev) => ({
                          ...prev,
                          beam_supports: enabled
                            ? {
                                1: { I_mm4: typicalI, E_mpa: 33000, L_m: 0 },
                                2: { I_mm4: typicalI, E_mpa: 33000, L_m: 0 },
                              }
                            : null,
                        }));
                      }}
                    />
                  }
                  label="Elastic beam support (compression-only contact)"
                />
                {formData.beam_supports && (
                  <Box
                    component="table"
                    sx={{
                      fontSize: '0.75rem',
                      borderCollapse: 'collapse',
                      '& th, & td': { px: 1, py: 0.25, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider' },
                      '& th': { fontWeight: 600 },
                    }}
                  >
                    <caption style={{ textAlign: 'left', marginBottom: 4, lineHeight: 1.4 }}>
                      Reference I for pinned edges x=0 / x=L (span L = {formData.W_m} m).
                      SS edge-beam check with q = {REF_EDGE_LINE_LOAD_KN_M} kN/m line load.
                      Coupled analysis differs — wet stage is often larger; SDL increment smaller.
                    </caption>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>I (mm⁴)</th>
                        <th>EI (kN·m²)</th>
                        <th>SS δ (mm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {beamStiffnessHintRows(formData.W_m || 10, 33000).map(({ label, I, EI, deltaMm }) => (
                        <tr key={label}>
                          <th>{label}</th>
                          <td>{formatHintI(I)}</td>
                          <td>{EI < 100 ? EI.toFixed(1) : Math.round(EI)}</td>
                          <td>{deltaMm.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                )}
              </Box>
              <Collapse in={!!formData.beam_supports}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Elastic bearing stiffness per metre along the support line (kN/m per m). Scaled to nodal springs by mesh spacing.">
                      <FormattedNumberField
                        size="small"
                        fullWidth
                        label="Bearing stiffness (kN/m per m)"
                        value={formData.bearing_k_per_m}
                        onChange={(v) => setFormData({ ...formData, bearing_k_per_m: v })}
                        decimalPlaces={0}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Optional capped bearing resistance per metre (kN/m). Contact force cannot exceed this line capacity.">
                      <FormattedNumberField
                        size="small"
                        fullWidth
                        label="Bearing capacity cap (kN/m)"
                        value={formData.bearing_capacity_per_m}
                        onChange={(v) => setFormData({ ...formData, bearing_capacity_per_m: v })}
                        decimalPlaces={0}
                      />
                    </Tooltip>
                  </Grid>
                  {[1, 2, 3, 4]
                    .filter((edgeId) => formData.edge_conditions?.[edgeId] !== 'free')
                    .map((edgeId) => (
                    <Grid item xs={12} md={6} key={edgeId}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Edge {EDGE_LABELS[edgeId]}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Tooltip
                            title={`Second moment (mm⁴). Exponent form OK (e.g. 2e9). Stiffness EI = E·I×10⁻⁹ kN·m². Low I vs stiff slab can slow the nonlinear solver.`}
                          >
                            <FormattedNumberField
                              size="small"
                              fullWidth
                              label="I (mm⁴)"
                              value={formData.beam_supports?.[edgeId]?.I_mm4 ?? 2e10}
                              onChange={(v) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  beam_supports: {
                                    ...prev.beam_supports,
                                    [edgeId]: {
                                      ...prev.beam_supports?.[edgeId],
                                      I_mm4: v ?? prev.beam_supports?.[edgeId]?.I_mm4 ?? 2e10,
                                      E_mpa: prev.beam_supports?.[edgeId]?.E_mpa ?? 33000,
                                      L_m: prev.beam_supports?.[edgeId]?.L_m ?? 0,
                                    },
                                  },
                                }))
                              }
                              decimalPlaces={0}
                            />
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}>
                            {(() => {
                              const spanM = beamSpanForEdge(
                                edgeId,
                                formData.L_m,
                                formData.W_m,
                                formData.beam_supports?.[edgeId]?.L_m ?? 0
                              );
                              const I = formData.beam_supports?.[edgeId]?.I_mm4 ?? 2e10;
                              const E = formData.beam_supports?.[edgeId]?.E_mpa ?? 33000;
                              const delta = ssBeamDeflectionMm(spanM, E, I);
                              const ei = eiKNm2(I, E);
                              return `Span ${spanM.toFixed(1)} m · EI ${ei < 100 ? ei.toFixed(1) : Math.round(ei)} kN·m² · SS δ ≈ ${delta < 10 ? delta.toFixed(2) : Math.round(delta)} mm @ ${REF_EDGE_LINE_LOAD_KN_M} kN/m`;
                            })()}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Tooltip title="Young's modulus (MPa). Typically 30000–35000 for concrete.">
                            <FormattedNumberField
                              size="small"
                              fullWidth
                              label="E (MPa)"
                              value={formData.beam_supports?.[edgeId]?.E_mpa ?? 33000}
                              onChange={(v) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  beam_supports: {
                                    ...prev.beam_supports,
                                    [edgeId]: {
                                      ...prev.beam_supports?.[edgeId],
                                      I_mm4: prev.beam_supports?.[edgeId]?.I_mm4 ?? 2e10,
                                      E_mpa: v ?? prev.beam_supports?.[edgeId]?.E_mpa ?? 33000,
                                      L_m: prev.beam_supports?.[edgeId]?.L_m ?? 0,
                                    },
                                  },
                                }))
                              }
                              decimalPlaces={0}
                            />
                          </Tooltip>
                        </Grid>
                        <Grid item xs={4}>
                          <Tooltip title="Beam length (m). 0 = use slab dimension along that edge.">
                            <FormattedNumberField
                              size="small"
                              fullWidth
                              label="L (m)"
                              value={formData.beam_supports?.[edgeId]?.L_m ?? 0}
                              onChange={(v) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  beam_supports: {
                                    ...prev.beam_supports,
                                    [edgeId]: {
                                      ...prev.beam_supports?.[edgeId],
                                      I_mm4: prev.beam_supports?.[edgeId]?.I_mm4 ?? 2e10,
                                      E_mpa: prev.beam_supports?.[edgeId]?.E_mpa ?? 33000,
                                      L_m: v ?? prev.beam_supports?.[edgeId]?.L_m ?? 0,
                                    },
                                  },
                                }))
                              }
                              decimalPlaces={1}
                            />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Interior support lines (optional)"
              subheader="Y-positions (m) of middle beams splitting the field. Used for two-sided WQ / middle-beam topology."
            />
            <CardContent>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    interior_support_y_m: [
                      ...(prev.interior_support_y_m || []),
                      Math.max(0.1, (prev.W_m || 10) / 2),
                    ],
                  }))
                }
              >
                Add interior support
              </Button>
              {(formData.interior_support_y_m || []).map((yPos, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                  <Tooltip title="Y coordinate along width (m), between 0 and W">
                    <FormattedNumberField
                      size="small"
                      label={`Support y${i + 1} (m)`}
                      value={yPos}
                      onChange={(v) =>
                        setFormData((prev) => ({
                          ...prev,
                          interior_support_y_m: prev.interior_support_y_m.map((val, j) => (j === i ? v : val)),
                        }))
                      }
                      decimalPlaces={2}
                      sx={{ width: 160 }}
                    />
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        interior_support_y_m: prev.interior_support_y_m.filter((_, j) => j !== i),
                      }))
                    }
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="WQ composite beam mode"
              subheader="Supported edges are analysed as WQ beams. Casting uses the steel section when unpropped; later loads use the composite section. Results include the required shear-connector steel area per metre."
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.wq_composite_mode}
                    onChange={(e) => setFormData({ ...formData, wq_composite_mode: e.target.checked })}
                  />
                }
                label="Enable WQ composite design"
              />
              {formData.wq_composite_mode && (
                <>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    γ_M0 and γ_V are recommended values. Through-bars are checked for diameter and anchorage only.
                    Confirm NDPs against licensed SFS-EN and the Finnish national annex before use.
                  </Alert>
                  <FormControlLabel
                    sx={{ display: 'block', mt: 1 }}
                    control={
                      <Switch
                        checked={formData.wq_beam?.propped_during_construction !== false}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            wq_beam: { ...prev.wq_beam, propped_during_construction: e.target.checked },
                          }))
                        }
                      />
                    }
                    label="Propped during construction"
                  />
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {[
                      ['web_width_mm', 'Box width (mm)', 0],
                      ['bottom_flange_width_mm', 'Bottom flange width (mm)', 0],
                      ['web_depth_mm', 'Web depth (mm)', 0],
                      ['web_thickness_mm', 'Web thickness (mm)', 1],
                      ['bottom_flange_thickness_mm', 'Flange thickness (mm)', 1],
                    ].map(([key, label, decimals]) => (
                      <Grid item xs={6} sm={4} key={key}>
                        <FormattedNumberField
                          fullWidth
                          size="small"
                          label={label}
                          value={formData.wq_beam?.[key]}
                          decimalPlaces={decimals}
                          onChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              wq_beam: { ...prev.wq_beam, [key]: v },
                            }))
                          }
                        />
                      </Grid>
                    ))}
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Steel grade"
                        value={formData.wq_beam?.steel_grade || 'S355'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            wq_beam: { ...prev.wq_beam, steel_grade: e.target.value },
                          }))
                        }
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.include_images}
                onChange={(e) => setFormData({ ...formData, include_images: e.target.checked })}
              />
            }
            label="Include figures"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
            Adds the deflection, moment, reaction, rebar, and tendon figures to the results and the print report.
            The plate solve still runs either way. Leave this off when you only need the checks.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
            onClick={handleRun}
            disabled={loading}
            sx={{ minWidth: 200, py: 1.5 }}
          >
            {loading ? 'Running...' : 'Run Slab Analysis'}
          </Button>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default SlabInputForm;
